import React, { useEffect, useState, useCallback } from "react";
import { Card, Container, Row, Col, Dropdown } from "react-bootstrap";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { AxiosInstance } from "../../axios";
import {
  job_calendar,
  job_bag,
  job_time,
  job_location,
} from "../../controller/Images";
import styles from "../../components/servicecard/Servicecard.module.css";
import Customstyles from "./JoinOurTeam.module.css";
import SeoPage from "../../components/seo/SeoPage";

const JoinOurTeam = () => {
  const [jobs, setJobs] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState({
    id: "",
    name: "All Departments",
  });

  const navigate = useNavigate();

  const [payload, setPayload] = useState({
    tag_id: "",
    job_id: "",
    department_id: "",
    role: "",
    tag_name: "",
    location: "",
    yearof_experience: "",
    start_date: "",
    end_date: "",
    search: "",
    job_status: 2,
  });

  const fetchJobs = useCallback(() => {
    AxiosInstance.post("/api/joinourteam/job/list", payload)
      .then((res) => {
        if (Array.isArray(res?.data.data)) {
          setJobs(res.data.data);
        } else {
          console.warn("Unexpected data format for job list", res.data);
          setJobs([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching jobs:", err);
        setJobs([]);
      });
  }, [payload]);

  useEffect(() => {
    AxiosInstance.get("/api/joinourteam/job/departmentlist")
      .then((res) => {
        if (Array.isArray(res?.data.Department)) {
          setDepartments([
            { id: "", name: "All Departments" },
            ...res.data.Department,
          ]);
        } else {
          setDepartments([{ id: "", name: "All Departments" }]);
        }
      })
      .catch(() => {
        setDepartments([{ id: "", name: "All Departments" }]);
      });

    fetchJobs();
  }, [fetchJobs]);

  const handleCardClick = (jobIdString) => {
    navigate(`/join-our-team-view/${jobIdString}`);
  };

  const handleDepartmentSelect = (departmentId, departmentName) => {
    setSelectedDepartment({ id: departmentId, name: departmentName });
    setPayload((prevPayload) => ({
      ...prevPayload,
      department_id: departmentId,
    }));
  };

  return (
    <>  
        <SeoPage
        title="Join Our Team | Careers That Inspire Growth & Innovation"
        description="Explore exciting career opportunities and join our team. Join our passionate team, work in a collaborative culture"
      />
     <Container
      fluid
      className="my-5"
      style={{ minHeight: "450px", height: "auto", paddingTop: "48px" }}
    >
      <div className="text-center">
        <h5 className={`mt-3 main_heading ${styles.service_heading}`}>
          Be part of our mission
        </h5>
        <p className={`main_subheading ${styles.service_subheading} p-0`}>
          We're looking for passionate people to join us on our mission. We
          value flat hierarchies, clear communication, and full ownership and
          responsibility.
        </p>
      </div>

      <div
        className="px-2"
        style={{ display: "flex", justifyContent: "flex-end" }}
      >
        <Dropdown
          onSelect={(eventKey) => {
            const [id, name] = eventKey.split("|");
            handleDepartmentSelect(id, name);
          }}
        >
          <Dropdown.Toggle
            variant="light"
            style={{
              border: "none",
              boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
              padding: "10px 16px",
              fontWeight: "600",
              color: "#142454",
              width: "100%",
              maxWidth: "300px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginRight: "70px",
            }}
          >
            <span>{selectedDepartment.name}</span>
            <span className="dropdown-toggle-icon" />
          </Dropdown.Toggle>

          <Dropdown.Menu align="end" style={{ width: "350px", zIndex: "1" }}>
            {departments.map((department) => (
              <Dropdown.Item
                eventKey={`${department.id}|${department.name}`}
                key={department.id}
              >
                {department.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <Row className="g-4 mt-4 align-items-stretch">
        {jobs.length > 0 ? (
          jobs.map((job, index) => {
            const skills = job.tag_name ? job.tag_name.split(",") : [];
            const displayedSkills = skills.slice(0, 3);
            const extraCount = skills.length > 3 ? skills.length - 3 : 0;

            return (
              <Col
                key={job.id || index}
                md={4}
                className={`d-flex`}
              >
                  <motion.div className="d-flex w-100"
                  initial={{ opacity: 0, scale: 0.2, filter: "blur(6px)" }}
                  animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                  transition={{
                    duration: 0.8,
                    ease: "easeInOut",
                    delay: index * 0.2,
                  }}
                >
                  <div
                    className={`pt-3 border-0 pt-3 rounded h-100 ${Customstyles.customCard}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleCardClick(job.job_id)}
                  >
                    <h5 className="main_subheading">{job.role}</h5>

                    <div className="text-muted pb-3">
                      <div className="d-flex align-items-center mb-1">
                        <img
                          src={job_location}
                          alt="location icon"
                          width="16"
                          className="me-2"
                        />
                        <p className="main_paragraph mb-0">{job.location}</p>
                      </div>

                      <div className="d-flex align-items-center mb-1">
                        <img
                          src={job_time}
                          alt="work arrangement icon"
                          width="16"
                          className="me-2"
                        />
                        <p className="main_paragraph mb-0">
                          {job.work_arrangement === "1"
                            ? "On-site"
                            : job.work_arrangement === "2"
                            ? "Remote"
                            : "Hybrid"}
                        </p>
                      </div>

                      <div className="d-flex align-items-center mb-1">
                        <img
                          src={job_bag}
                          alt="experience icon"
                          width="16"
                          className="me-2"
                        />
                        <p className="main_paragraph mb-0">
                          {job.yearof_experience}{" "}
                          {job.yearof_experience === "1" ? "year" : "years"}
                        </p>
                      </div>

                      <div className="d-flex align-items-center mb-1">
                        <img
                          src={job_calendar}
                          alt="calendar icon"
                          width="16"
                          className="me-2"
                        />
                        <p className="main_paragraph mb-0">
                          {(() => {
                            const date = new Date(job.created_date);
                            const day = String(date.getDate()).padStart(2, "0");
                            const month = String(date.getMonth() + 1).padStart(
                              2,
                              "0"
                            );
                            const year = date.getFullYear();
                            return `${day}/${month}/${year}`;
                          })()}
                        </p>
                      </div>
                    </div>

                    {/* 
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {displayedSkills.map((skill, i) => (
                        <div key={i} className={Customstyles.customBadge}>
                          {skill.trim()}
                        </div>
                      ))}
                      {extraCount > 0 && (
                        <div className={Customstyles.customcount}>
                          +{extraCount} more
                        </div>
                      )}
                    </div> */}
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {displayedSkills.slice(0, 3).map((skill, i) => (
                        <div
                          key={i}
                          className={Customstyles.customBadge}
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "80px",
                          }}
                          title={skill.trim()} // tooltip for full text
                        >
                          {skill.trim()}
                        </div>
                      ))}

                      {skills.length > 3 && (
                        <div className={Customstyles.customcount}>+more</div>
                      )}
                    </div>
                  </div>
                </motion.div>
              </Col>
            );
          })
        ) : (
          <div>
            <p
              className="text-center mt-5"
              style={{ color: "#142454", fontWeight: 500 }}
            >
              There are no job openings available at the moment.
            </p>
          </div>
        )}
      </Row>
    </Container>
    </>
 
  );
};

export default JoinOurTeam;
